import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import {
  getStandardCosts,
  getCostElements,
  type ProductionCostRecord,
  type CostElementItem,
} from '@/services/costingService';
import CalculateCostModal from '../orders/components/CalculateCostModal';
import CostSheetPrintModal from '../orders/components/CostSheetPrintModal';
import { useWarehouseStore } from '@/stores/warehouseStore';

function formatCurrency(value: number): string {
  return `₹${(value || 0).toLocaleString()}`;
}

function getTypeBadge(type: string) {
  switch (type) {
    case 'MATERIAL':
      return 'bg-blue-100 text-blue-700';
    case 'LABOUR':
      return 'bg-emerald-100 text-emerald-700';
    case 'OVERHEAD':
      return 'bg-purple-100 text-purple-700';
    case 'REJECTION':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}

export default function StandardCostPage() {
  const navigate = useNavigate();
  const { selectedWarehouseId } = useWarehouseStore();
  const [loading, setLoading] = useState(true);
  const [standardCosts, setStandardCosts] = useState<ProductionCostRecord[]>([]);
  const [selectedCostId, setSelectedCostId] = useState<string | null>(null);
  const [selectedCost, setSelectedCost] = useState<ProductionCostRecord | null>(null);
  const [selectedElements, setSelectedElements] = useState<CostElementItem[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showCalcModal, setShowCalcModal] = useState(false);
  const [printCost, setPrintCost] = useState<ProductionCostRecord | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await getStandardCosts('', '2024-2025', selectedWarehouseId);
      if (res && res.success && res.data) {
        setStandardCosts(res.data);
      }
    } catch (err) {
      console.error("Failed to load standard costs:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedWarehouseId]);

  const handleSaveCost = (newRecord: ProductionCostRecord) => {
    setShowCalcModal(false);
    showToast(`Standard Cost calculated and saved for ${newRecord.productionOrderNumber}`);
    loadData();
  };

  const handleOpenDetail = async (id: string) => {
    setSelectedCostId(id);
    setDetailLoading(true);
    try {
      const res = await getCostElements(id);
      if (res && res.success && res.data) {
        setSelectedCost(res.data.costRecord);
        setSelectedElements(res.data.elements);
      }
    } catch (err) {
      console.error("Failed to load cost elements:", err);
    } finally {
      setDetailLoading(false);
    }
  };

  const summary = useMemo(() => {
    const totalStdCost = standardCosts.reduce((s, c) => s + c.totalCost, 0);
    const totalQty = standardCosts.reduce((s, c) => s + c.producedQty, 0);
    const totalMaterial = standardCosts.reduce((s, c) => s + c.materialCost, 0);
    const totalLabourOverhead = standardCosts.reduce((s, c) => s + c.labourCost + c.overheadCost, 0);
    const avgCostPerUnit = totalQty > 0 ? totalStdCost / totalQty : 0;

    return { totalStdCost, avgCostPerUnit, totalMaterial, totalLabourOverhead, totalQty };
  }, [standardCosts]);

  return (
    <AppLayout>
      <div className="space-y-6 p-4 md:p-6">
        {/* Toast Notification */}
        {toast && (
          <div className="fixed top-4 right-4 z-50 bg-gray-900 text-white text-sm px-4 py-2.5 rounded-lg shadow-xl flex items-center gap-2">
            <i className="ri-checkbox-circle-fill text-green-400 text-base" />
            <span>{toast}</span>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/manufacturing/costing')}
              className="p-2 text-gray-600 hover:text-gray-900 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer shadow-sm transition-colors"
              title="Back to Dashboard"
            >
              <i className="ri-arrow-left-line text-lg" />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Standard Cost</h1>
              <p className="text-sm text-gray-500 mt-0.5">Planned cost database records based on BOM and routing</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowCalcModal(true)}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 cursor-pointer whitespace-nowrap shadow-sm"
            >
              <i className="ri-calculator-line text-base" />
              Calculate Standard Cost
            </button>
            <button
              onClick={() => window.print()}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-printer-line" />
              Print All
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-xs text-gray-500 mb-1">Total Standard Cost</div>
            <div className="text-lg font-semibold text-gray-900">{formatCurrency(summary.totalStdCost)}</div>
            <div className="mt-2 text-xs text-gray-500">
              Across {standardCosts.length} production order{standardCosts.length !== 1 ? 's' : ''}
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-xs text-gray-500 mb-1">Avg Standard Cost / Unit</div>
            <div className="text-lg font-semibold text-gray-900">{formatCurrency(Math.round(summary.avgCostPerUnit))}</div>
            <div className="mt-2 text-xs text-gray-500">
              Total {summary.totalQty} units across orders
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-xs text-gray-500 mb-1">Total Material Cost</div>
            <div className="text-lg font-semibold text-gray-900">{formatCurrency(summary.totalMaterial)}</div>
            <div className="mt-2 text-xs text-gray-500">
              Across all production orders
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-xs text-gray-500 mb-1">Total Labour + Overhead</div>
            <div className="text-lg font-semibold text-gray-900">{formatCurrency(summary.totalLabourOverhead)}</div>
            <div className="mt-2 text-xs text-gray-500">
              Labour + Overhead combined
            </div>
          </div>
        </div>

        {/* Standard Cost Table */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900">Standard Cost Records</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-3 py-2.5 text-left font-medium text-gray-500 whitespace-nowrap">PO Number</th>
                  <th className="px-3 py-2.5 text-left font-medium text-gray-500 whitespace-nowrap">Product</th>
                  <th className="px-3 py-2.5 text-right font-medium text-gray-500 whitespace-nowrap">Qty</th>
                  <th className="px-3 py-2.5 text-right font-medium text-gray-500 whitespace-nowrap">Std Material</th>
                  <th className="px-3 py-2.5 text-right font-medium text-gray-500 whitespace-nowrap">Std Labour</th>
                  <th className="px-3 py-2.5 text-right font-medium text-gray-500 whitespace-nowrap">Std Overhead</th>
                  <th className="px-3 py-2.5 text-right font-medium text-gray-500 whitespace-nowrap">Std Total</th>
                  <th className="px-3 py-2.5 text-right font-medium text-gray-500 whitespace-nowrap">Cost / Unit</th>
                  <th className="px-3 py-2.5 text-center font-medium text-gray-500 whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {standardCosts.map((cost) => (
                  <tr key={cost.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2.5 text-gray-900 font-medium whitespace-nowrap">{cost.productionOrderNumber}</td>
                    <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">
                      {cost.productName}
                      {cost.variantName ? <span className="block text-gray-400 text-[10px]">{cost.variantName}</span> : null}
                    </td>
                    <td className="px-3 py-2.5 text-right text-gray-600 whitespace-nowrap">{cost.producedQty} {cost.unit}</td>
                    <td className="px-3 py-2.5 text-right text-gray-600 whitespace-nowrap">{formatCurrency(cost.materialCost)}</td>
                    <td className="px-3 py-2.5 text-right text-gray-600 whitespace-nowrap">{formatCurrency(cost.labourCost)}</td>
                    <td className="px-3 py-2.5 text-right text-gray-600 whitespace-nowrap">{formatCurrency(cost.overheadCost)}</td>
                    <td className="px-3 py-2.5 text-right font-semibold text-gray-900 whitespace-nowrap">{formatCurrency(cost.totalCost)}</td>
                    <td className="px-3 py-2.5 text-right text-gray-600 whitespace-nowrap">{formatCurrency(cost.costPerUnit)}/unit</td>
                    <td className="px-3 py-2.5 text-center whitespace-nowrap">
                      <div className="flex items-center justify-center gap-1.5">
                        <button
                          onClick={() => handleOpenDetail(cost.id)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 cursor-pointer"
                          title="View Details"
                        >
                          <i className="ri-eye-line" />
                          View
                        </button>
                        <button
                          onClick={() => setPrintCost(cost)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 cursor-pointer"
                          title="Print Cost Sheet"
                        >
                          <i className="ri-printer-line" />
                          Print
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {standardCosts.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-3 py-6 text-center text-gray-500 text-sm">
                      {loading ? 'Loading standard cost records...' : 'No standard cost records found in database.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Slide-over: Standard Cost Detail */}
        {selectedCostId && (
          <div className="fixed inset-0 z-50">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setSelectedCostId(null)}
            />
            <div className="absolute right-0 top-0 bottom-0 w-full max-w-xl bg-white shadow-lg overflow-y-auto">
              <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-5 py-4 flex items-center justify-between">
                <h2 className="text-base font-semibold text-gray-900">
                  Standard Cost Details — {selectedCost?.productionOrderNumber || 'Loading...'}
                </h2>
                <button
                  onClick={() => setSelectedCostId(null)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer"
                >
                  <i className="ri-close-line text-lg text-gray-500" />
                </button>
              </div>

              {detailLoading ? (
                <div className="p-8 text-center text-gray-500 text-sm">Loading details...</div>
              ) : selectedCost ? (
                <div className="px-5 py-4 space-y-5">
                  <section>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Order Info</h4>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <span className="text-gray-500">PO Number</span>
                        <div className="text-gray-900 font-medium mt-0.5">{selectedCost.productionOrderNumber}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">Product</span>
                        <div className="text-gray-900 font-medium mt-0.5">{selectedCost.productName}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">Quantity</span>
                        <div className="text-gray-900 font-medium mt-0.5">{selectedCost.producedQty} {selectedCost.unit}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">Total Standard Cost</span>
                        <div className="text-gray-900 font-semibold mt-0.5">{formatCurrency(selectedCost.totalCost)}</div>
                      </div>
                    </div>
                  </section>

                  <section>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Cost Elements</h4>
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="px-3 py-2 text-left font-medium text-gray-500">Description</th>
                            <th className="px-3 py-2 text-center font-medium text-gray-500">Type</th>
                            <th className="px-3 py-2 text-right font-medium text-gray-500">Qty</th>
                            <th className="px-3 py-2 text-right font-medium text-gray-500">Rate</th>
                            <th className="px-3 py-2 text-right font-medium text-gray-500">Amount</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {selectedElements.map((el) => (
                            <tr key={el.id}>
                              <td className="px-3 py-2 text-gray-900">{el.description}</td>
                              <td className="px-3 py-2 text-center">
                                <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${getTypeBadge(el.type)}`}>
                                  {el.type}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-right text-gray-600">{el.qty} {el.unit}</td>
                              <td className="px-3 py-2 text-right text-gray-600">{formatCurrency(el.rate)}</td>
                              <td className="px-3 py-2 text-right text-gray-900 font-medium">{formatCurrency(el.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                </div>
              ) : null}
            </div>
          </div>
        )}

        {/* Calculate Cost Modal */}
        {showCalcModal && (
          <CalculateCostModal
            initialMethod="STANDARD"
            onClose={() => setShowCalcModal(false)}
            onSave={handleSaveCost}
          />
        )}

        {/* Cost Sheet Print Modal */}
        {printCost && (
          <CostSheetPrintModal
            cost={printCost}
            onClose={() => setPrintCost(null)}
          />
        )}
      </div>
    </AppLayout>
  );
}